import EmptyRecods from "components/Animations/EmptyRecods";
import WaitingAnimation from "components/Animations/WaitingAnimation";
import {
  isObligatoryTag,
  SurveyQuestionType,
  SurveyTagLabel,
  SurveyTagType
} from "constants/enums/StudentSurveyEnum";
import { uniq, uniqBy } from "lodash";
import React, { useEffect, useRef } from "react";
import { useState } from "react";
import apiCall from "services/apiCall";
import { getSurveyTags } from "services/survey-service";
import SurveyTag from "./SurveyTag";
import SurveyQuestion from "./SurveyQuestion";
import SurveySubmitModal from "./SurveySubmitModal";
import { getSchoolById } from "services/school-service";
import useStudentServiceParams from "hooks/useStudentServiceParams";
import { forwardRef } from "react";
import { useImperativeHandle } from "react";

const SurveyDetail = forwardRef(({ survey, onHide, preloadedTags }, ref) => {
  const [answer, setAnswer] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [questionCommentList, setQuestionCommentList] = useState([]);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [surveyTags, setSurveyTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  const { params } = useStudentServiceParams({
    schoolId: "",
    studentDetails: ""
  });
  const [studentSchoolType, setStudentSchoolType] = useState(null);
  const [missingRequiredFields, setMissingRequiredFields] = useState([]);

  const surveyRef = useRef(null);
  const questionRefs = useRef({});

  useEffect(() => {
    if (selectedCategory?.taggedId) {
      surveyRef?.current?.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }, [selectedCategory?.taggedId]);

  useEffect(() => {
    if (params.schoolId) {
      getSchoolByIdFromApi();
      // If preloaded tags exist, use them instead of fetching
      if (preloadedTags && preloadedTags.length > 0) {
        setTagList(preloadedTags);
      } else {
        getSurveyTagsFromApi();
      }
      setSurveyTags(uniq(survey.questions.reduce((prev, now) => [...prev, ...now.tags], [])));
    }
  }, [JSON.stringify(survey), params.schoolId]);

  useEffect(() => {
    if (surveyTags.length && tagList.length && studentSchoolType) prepareCategory();
  }, [surveyTags, tagList, studentSchoolType]);

  const getSchoolByIdFromApi = () => {
    apiCall({
      service: getSchoolById,
      serviceParams: params?.schoolId,
      onSuccess: (res) => {
        setStudentSchoolType(res?.data?.institutionType);
      },
      onError: (res) => {}
    });
  };

  const prepareCategory = () => {
    const tempCategoryList = [];
    const teacherBranchList = uniqBy(
      tagList?.filter((tag) => tag?.tag === SurveyTagType.TEACHER),
      "branch"
    );
    surveyTags.forEach((tag, index) => {
      const questionList = survey?.questions
        ?.filter((question) =>
          question?.schoolTypes
            ?.find((type) => type?.name === studentSchoolType)
            ?.levels.find((level) => level.code === params?.studentDetails?.levelInfo?.code)
        )
        ?.filter((question) => question?.tags?.find((questionTag) => questionTag === tag));
      const questionCount = questionList?.length;
      if (tag !== "TEACHER") {
        const tempTagList = tagList.filter((personel) => personel.tag === tag);
        if (tempTagList.length && questionCount) {
          tempCategoryList.push({
            tag,
            tagList: tempTagList,
            questionCount,
            questionList
          });
        } else if (!tempTagList.length && isObligatoryTag(tag) && questionCount) {
          tempCategoryList.push({
            tag,
            tagList: [
              {
                tag,
                taggedId: tag + "_" + index,
                taggedName: SurveyTagLabel[tag] || "-",
                branch: null,
                description: null
              }
            ],
            questionCount,
            questionList
          });
        }
      } else {
        teacherBranchList.forEach((branch) => {
          const tempTagList = tagList.filter(
            (personel) => personel.description === branch?.description
          );
          if (questionCount) {
            tempCategoryList.push({
              tag: branch?.description,
              tagList: tempTagList,
              questionCount,
              questionList
            });
          }
        });
      }
    });

    setCategoryList(tempCategoryList);
  };

  const getSurveyTagsFromApi = () => {
    setTagLoading(true);
    apiCall({
      service: getSurveyTags,
      onSuccess: (res) => {
        if (Array.isArray(res.data)) {
          setTagList(res.data);
        }
        setTagLoading(false);
      },
      onError: (res) => {
        setTagLoading(false);
      }
    });
  };

  const checkBestOrWorst = (question, option) => {
    const options = question.options;
    if (Array.isArray(options) && options.length > 0) {
      const MAX_SCORE = Math.max(...options.map((opt) => parseInt(opt?.score)));
      const MIN_SCORE = Math.min(...options.map((opt) => parseInt(opt?.score)));
      if (
        option?.score === MAX_SCORE &&
        options.filter((opt) => opt?.score === MAX_SCORE).length === 1
      ) {
        return 1;
      } else if (
        option?.score === MIN_SCORE &&
        options.filter((opt) => opt?.score === MIN_SCORE).length === 1
      ) {
        return -1;
      }
      return 0;
    }
  };

  const answerSetter = (question, option, type) => {
    const studentAnswer = {
      bestOrWorst: type !== SurveyQuestionType.OPEN ? checkBestOrWorst(question, option) : 0,
      optionId: type !== SurveyQuestionType.OPEN && option?.id,
      questionId: question?.id,
      score: option?.score,
      tag: selectedCategory.tag,
      taggedName: selectedCategory?.taggedName,
      taggedId: selectedCategory?.taggedId,
      questionType:
        type === SurveyQuestionType.OPEN
          ? SurveyQuestionType.OPEN
          : SurveyQuestionType.MULTI_SELECT,
      questionText: type === SurveyQuestionType.OPEN && option,
      branchInfo: {
        id: selectedCategory?.branch,
        name: selectedCategory?.description,
        code: selectedCategory?.description
      },
      questionComment: "",
      job: question?.job
    };
    const isThereAsnwer = answer.find(
      (_answer) =>
        _answer?.taggedId === selectedCategory?.taggedId &&
        _answer?.questionId === question?.id &&
        _answer?.branchInfo?.id === selectedCategory?.branch
    );
    if (!isThereAsnwer) {
      setAnswer((prev) => [...prev, studentAnswer]);
    } else {
      setAnswer((prev) => [
        ...prev.filter(
          (pAnswer) =>
            !(
              pAnswer?.taggedId === studentAnswer?.taggedId &&
              pAnswer?.questionId === studentAnswer?.questionId
            )
        ),
        studentAnswer
      ]);
    }
  };

  const commentSetter = (question, comment) => {
    const _comment = {
      taggedId: selectedCategory?.taggedId,
      questionId: question?.id,
      branchInfo: {
        id: selectedCategory?.branch,
        name: selectedCategory?.description,
        code: selectedCategory?.description
      },
      comment
    };

    const isExist = questionCommentList
      .filter(
        (_com) =>
          _com?.taggedId === selectedCategory?.taggedId &&
          _com?.branchInfo?.id === selectedCategory?.branch
      )
      .find((_com) => _com?.questionId === question?.id);

    if (!isExist) {
      setQuestionCommentList((prev) => [...prev, _comment]);
    } else {
      //
      setQuestionCommentList((prev) => [
        ...prev.filter(
          (pComment) =>
            !(
              pComment?.taggedId === selectedCategory?.taggedId &&
              pComment?.branchInfo?.id === selectedCategory?.branch &&
              pComment?.questionId === question?.id
            )
        ),
        _comment
      ]);
    }
  };

  const clearAnswer = (question) =>
    setAnswer(
      answer?.filter(
        (_answer) =>
          !(
            _answer?.taggedId === selectedCategory?.taggedId &&
            _answer?.branchInfo?.id === selectedCategory?.branch &&
            _answer.questionId === question?.id
          )
      )
    );

  useImperativeHandle(ref, () => ({
    submit: () => {
      const missing = checkRequiredFields();
      if (missing.length > 0) {
        setMissingRequiredFields(missing);

        if (missing.length > 0) {
          const firstMissingCategory = missing[0].category;
          setSelectedCategory(firstMissingCategory.tagList[0]);

          setTimeout(() => {
            const firstMissingQuestion = missing[0].missingQuestions[0];

            if (questionRefs.current[firstMissingQuestion.id]) {
              questionRefs.current[firstMissingQuestion.id].scrollIntoView({
                behavior: "smooth",
                block: "center"
              });
            } else {
              surveyRef?.current?.scrollTo({
                top: 0,
                behavior: "smooth"
              });
            }
          }, 300);
        }

        return;
      }
      setShowSubmitModal(true);
    }
  }));

  const checkRequiredFields = () => {
    const missingFields = [];

    if (Array.isArray(categoryList) && categoryList.length) {
      categoryList.forEach((category) => {
        const requiredQuestions = category.questionList.filter((q) => q.isRequired === true);

        if (requiredQuestions.length > 0) {
          const unansweredRequiredQuestions = requiredQuestions.filter((question) => {
            const isAnswered = category.tagList.some((tag) => {
              return answer.some(
                (ans) =>
                  ans.taggedId === tag.taggedId &&
                  ans.branchInfo?.id === tag.branch &&
                  ans.questionId === question.id
              );
            });

            return !isAnswered;
          });

          if (unansweredRequiredQuestions.length > 0) {
            missingFields.push({
              category: category,
              missingQuestions: unansweredRequiredQuestions
            });
          }
        }
      });
    }

    return missingFields;
  };

  useEffect(() => {
    if (missingRequiredFields.length > 0) {
      setMissingRequiredFields(checkRequiredFields());
    }
  }, [answer]);

  const isQuestionAnswered = (question, category) => {
    return answer.some(
      (ans) =>
        ans.taggedId === category?.taggedId &&
        ans.branchInfo?.id === category?.branch &&
        ans.questionId === question.id
    );
  };

  return (
    <div className="row m-0 p-4 fc-scroller">
      <div className="col-md-4 pb-2 p-0" style={{ minHeight: "75vh", maxHeight: "75vh" }}>
        <div
          style={{ maxHeight: "93%", minHeight: "93%", overflowY: "auto" }}
          className="scroll p-2 px-4 mb-2"
        >
          {tagLoading ? (
            <WaitingAnimation />
          ) : Array.isArray(categoryList) && categoryList.length ? (
            categoryList.map((category, index) => (
              <SurveyTag
                key={category?.tag}
                category={category}
                index={index}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                answer={answer}
                missingRequiredFields={missingRequiredFields}
              />
            ))
          ) : (
            "Değerlendirilecek kategori bulunmuyor."
          )}
        </div>
      </div>

      <div
        ref={surveyRef}
        className="col-md-8 scroll rounded p-0 m-0"
        style={{ maxHeight: "75vh", overflowY: "auto", background: "#F4F5F8" }}
      >
        {selectedCategory && (
          <div className="pl-4 py-2 font-weight-bold" style={{ background: "#DEDEFF" }}>
            {selectedCategory?.taggedName}
          </div>
        )}
        {!selectedCategory ? (
          <EmptyRecods message="Sol tarafdaki menüden kategori seçiniz." />
        ) : (
          categoryList
            .find((category) =>
              category?.tagList.find((_tag) => _tag.tag === selectedCategory?.tag)
            )
            ?.questionList?.map((question, index) => (
              <div
                key={question?.id}
                ref={(el) => (questionRefs.current[question.id] = el)}
                className={`${
                  missingRequiredFields.some((item) =>
                    item.missingQuestions.some((q) => q.id === question.id)
                  ) && !isQuestionAnswered(question, selectedCategory)
                    ? "border-left border-danger bg-light shadow-sm position-relative mb-2"
                    : ""
                }`}
              >
                {missingRequiredFields.some((item) =>
                  item.missingQuestions.some((q) => q.id === question.id)
                ) &&
                  !isQuestionAnswered(question, selectedCategory) && (
                    <div
                      className="position-absolute"
                      style={{ top: "10px", right: "10px", zIndex: 5 }}
                    >
                      <span className="badge badge-danger">Zorunlu</span>
                    </div>
                  )}
                <SurveyQuestion
                  survey={survey}
                  answer={answer}
                  question={question}
                  answerSetter={answerSetter}
                  commentSetter={commentSetter}
                  selectedCategory={selectedCategory}
                  questionCommentList={questionCommentList}
                  questionOrder={index + 1}
                  clearAnswer={clearAnswer}
                />
              </div>
            ))
        )}
      </div>

      {showSubmitModal && (
        <SurveySubmitModal
          onHide={() => setShowSubmitModal(false)}
          closeSurveyModal={onHide}
          show={showSubmitModal}
          answer={answer}
          categoryList={categoryList}
          survey={survey}
          surveyTags={surveyTags}
          questionCommentList={questionCommentList}
          tagList={tagList}
        />
      )}
    </div>
  );
});

export default SurveyDetail;
